import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import {
  getPayment,
  listPayments,
  patchPayment,
  createPayment,
  deletePayment,
  getPaymentsByAssetId
} from '../Api/Payment/PaymentApi';
import { addError } from './Error';
import { ApiAdvancedFilter, PaginatedResponse } from './GeneralStore';
import { hasDataOrHigherPermissions, hasEngineerOrHigherPermissions, User } from './User';
import { DrawerData } from './Drawer';
import { CallResult } from '../Api/APIService';

export interface Payment {
  id: string;
  date: Date;
  assetId: string;
  amount: number;
  createdAt: Date;
  updatedAt: Date;
  assetName: string;
  assetNumber: string;
  commissionsStatus: string;
  supplierName: string;
  salesAgentName: string;
  customerName: string;
  customerId: string;
  recipientId: string;
  recipientName: string;
  recipientType: string;
  createdBy: User | null;
  modifiedBy: User | null;
}

const inboundFetchNumberSubject = new BehaviorSubject<number>(0);
const paymentSubject = new BehaviorSubject<Payment>({} as Payment);
const paymentDetailSubject = new BehaviorSubject<DrawerData | undefined>(undefined);
const tallyPaymentsSubject = new BehaviorSubject<PaginatedResponse<Payment>>({} as PaginatedResponse<Payment>);
const assetPaymentsSubject = new BehaviorSubject<{ [key: string]: Payment[] }>({});
const paymentLoadingSubject = new BehaviorSubject<boolean>(false);
const tallyPaymentsLoadingSubject = new BehaviorSubject<boolean>(false);
const updatingPaymentSubject = new BehaviorSubject<boolean>(false);
const currentFiltersSubject = new BehaviorSubject<ApiAdvancedFilter | undefined>(undefined);

const clearError = () => addError('payment', undefined);

export const getPaymentSubject = (): BehaviorSubject<Payment> => paymentSubject;
export const getPaymentLoadingSubject = (): BehaviorSubject<boolean> => paymentLoadingSubject;
export const getPaymentDetailSubject = (): BehaviorSubject<DrawerData | undefined> => paymentDetailSubject;
export const getTallyPaymentsSubject = (): BehaviorSubject<PaginatedResponse<Payment>> => tallyPaymentsSubject;
export const getTallyPaymentsLoadingSubject = (): BehaviorSubject<boolean> => tallyPaymentsLoadingSubject;
export const updatePaymentDetail = (paymentData: DrawerData) => paymentDetailSubject.next(paymentData);
export const getAssetPaymentsSubject = (): BehaviorSubject<{ [key: string]: Payment[] }> => assetPaymentsSubject;

export const getPaymentById = async (id: string): Promise<Payment> => {
  const payment = tallyPaymentsSubject?.getValue()?.data?.find((a) => a?.id === id);

  if (payment) {
    return payment;
  } else {
    const { data, error } = await getPayment(id);
    if (error) addError('payment', error.message);
    if (data) return data;
  }

  return {} as Payment;
};

export const fetchPayments = async (filters?: ApiAdvancedFilter, forceRefresh = false) => {
  if (!hasDataOrHigherPermissions()) {
    addError('payment', 'You do not have permission to view payments.');
    return;
  }
  const newFetchNumber = inboundFetchNumberSubject.getValue() + 1;
  inboundFetchNumberSubject.next(newFetchNumber);
  if (tallyPaymentsSubject.getValue()?.data?.length > 0 && !forceRefresh) return;
  tallyPaymentsLoadingSubject.next(true);
  currentFiltersSubject.next(filters);
  const { data, error } = await listPayments(filters);
  const currentFetchNumber = inboundFetchNumberSubject.getValue();
  if (data && newFetchNumber === currentFetchNumber) {
    clearError();
    tallyPaymentsSubject.next(data);
  }
  if (error) addError('payment', error.message);

  newFetchNumber === currentFetchNumber && tallyPaymentsLoadingSubject.next(false);
};

export const fetchPayment = async (id: string, updateDrawer?: boolean): Promise<void | number> => {
  if (!hasDataOrHigherPermissions()) {
    addError('payment', 'You do not have permission to view payments.');
    return;
  }
  paymentLoadingSubject.next(true);

  const { data, error } = await getPayment(id);

  if (data) {
    clearError();
    const subject = getPaymentSubject().getValue();
    if (!updateDrawer || !subject.id || subject.id === data.id) paymentSubject.next(data);
    const detailSubject = getPaymentDetailSubject().getValue() as Payment | undefined;
    if (updateDrawer || detailSubject?.id === data.id) paymentDetailSubject.next(data);
  }
  if (error) {
    addError('payment', error.message);
    const statusCode = error.errorResponse?.status || 500;
    return statusCode;
  }

  paymentLoadingSubject.next(false);
};

export const updatePayment = async (payment: Payment, updateDrawer?: boolean) => {
  if (!hasDataOrHigherPermissions()) {
    addError('payment', 'You do not have permission to edit payments.');
    return { data: null, error: { message: 'Permission denied' } } as CallResult<Payment>;
  }
  updatingPaymentSubject.next(true);

  const { data, error } = await patchPayment(payment);
  updatingPaymentSubject.next(false);

  if (data) {
    clearError();
    fetchPayment(data.id, updateDrawer);
    const filters = currentFiltersSubject.getValue();
    fetchPayments(filters, !!filters);
    fetchPaymentsByAssetId(payment.assetId);
  }
  if (error) addError('payment', error.message);

  return { data, error };
};

export const createNewPayment = async (payment: Payment) => {
  if (!hasDataOrHigherPermissions()) {
    addError('payment', 'You do not have permission to create payments.');
    return;
  }
  updatingPaymentSubject.next(true);

  const { data, error } = await createPayment(payment);

  if (data) {
    clearError();
    const filters = currentFiltersSubject.getValue();
    fetchPayments(filters, !!filters);
    fetchPaymentsByAssetId(payment.assetId);
  }
  if (error) addError('payment', error.message);

  updatingPaymentSubject.next(false);
  return { data, error };
};

export const fetchPaymentsByAssetId = async (assetId: string) => {
  if (!hasDataOrHigherPermissions()) {
    addError('payment', 'You do not have permission to view payments.');
    return;
  }

  const { data, error } = await getPaymentsByAssetId(assetId);
  if (data) {
    clearError();
    assetPaymentsSubject.next({ ...assetPaymentsSubject.getValue(), [assetId]: data });
  }
  if (error) addError('payment', error.message);
};

export const deletePaymentById = async (payment: Payment) => {
  if (!hasEngineerOrHigherPermissions()) {
    addError('payment', 'You do not have permission to delete payments.');
    return;
  }

  const { data, error } = await deletePayment(payment.id);
  if (data) {
    clearError();
    tallyPaymentsSubject.next({
      ...tallyPaymentsSubject.getValue(),
      data: tallyPaymentsSubject.getValue().data.filter((p) => p.id !== payment.id)
    });
    fetchPaymentsByAssetId(payment.assetId);
  }
  if (error) addError('payment', error.message);
};
